/**
 * @file ImageCropper.tsx
 * @description Inline-кроп загружаемой картинки (phase19.8.2, react-easy-crop).
 *
 * Рендерится прямо в панели редактора блока (не модалка — фидбек 19.8.1):
 * рамка + zoom + выбор пропорции; «Обрезать и загрузить» рендерит crop в
 * canvas → JPEG-blob; «Оригинал» отдаёт файл без обрезки.
 */

import { useCallback, useEffect, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";

import { Button } from "../ui/button";

/**
 * Пресеты кадрирования. `undefined` — свободный кроп (phase19.8.3: блок
 * подстраивается под пропорции кадра, жёсткого «формата места» больше нет).
 */
const ASPECTS: Array<{ label: string; value: number | undefined }> = [
  { label: "Свободно", value: undefined },
  { label: "21:9", value: 21 / 9 },
  { label: "16:9", value: 16 / 9 },
  { label: "3:2", value: 3 / 2 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
  { label: "3:4", value: 3 / 4 },
];

/** Рендер выбранной области в JPEG-blob. */
async function cropToBlob(objectUrl: string, area: Area): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Не удалось прочитать картинку"));
    img.src = objectUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D недоступен");
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Не удалось обрезать"))),
      "image/jpeg",
      0.92,
    );
  });
}

interface ImageCropperProps {
  file: File;
  onCancel: () => void;
  /**
   * Файл к загрузке + пропорции кадра (width/height): их сохраняет блок,
   * чтобы подстроить свою высоту (phase19.8.3).
   */
  onDone: (file: File, aspect: number) => void;
  busy?: boolean;
  /** Подсказка «в этом блоке кадр смотрится лучше всего» (не ограничение). */
  hintAspect?: number;
  /** Коридор блока — за его пределами кадр обрежется (для подсказки). */
  hintRange?: { min: number; max: number };
}

export function ImageCropper({
  file,
  onCancel,
  onDone,
  busy = false,
  hintAspect,
  hintRange,
}: ImageCropperProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);

  // ObjectURL создаётся в эффекте (не useMemo): StrictMode-цикл
  // mount→cleanup→mount иначе отзывал бы закешированный URL.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  async function onApply() {
    if (!areaPixels || objectUrl === null) return;
    setWorking(true);
    try {
      const blob = await cropToBlob(objectUrl, areaPixels);
      const name = file.name.replace(/\.[^.]+$/, "") + "-crop.jpg";
      onDone(new File([blob], name, { type: "image/jpeg" }), areaPixels.width / areaPixels.height);
    } finally {
      setWorking(false);
    }
  }

  /** Загрузка без обрезки — пропорции берём из натурального размера файла. */
  async function onOriginal() {
    if (objectUrl === null) return;
    setWorking(true);
    try {
      const natural = await new Promise<number>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth / img.naturalHeight);
        img.onerror = () => resolve(1);
        img.src = objectUrl;
      });
      onDone(file, natural);
    } finally {
      setWorking(false);
    }
  }

  const disabled = busy || working;
  /** Кадр вне коридора блока → предупреждение об обрезке. */
  const cropAspect =
    areaPixels && areaPixels.height > 0 ? areaPixels.width / areaPixels.height : null;
  const outOfRange =
    hintRange !== undefined &&
    cropAspect !== null &&
    (cropAspect < hintRange.min || cropAspect > hintRange.max);

  return (
    <div className="flex flex-col gap-2 rounded-md bg-surface-low p-2">
      <div className="relative h-64 w-full overflow-hidden rounded bg-black/80">
        {objectUrl !== null ? (
          <Cropper
            image={objectUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-on-surface-muted">Масштаб</span>
        <input
          type="range"
          min={1}
          max={4}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 accent-[var(--primary)]"
          aria-label="Масштаб"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {ASPECTS.map((a) => {
          const isHint =
            hintAspect !== undefined &&
            a.value !== undefined &&
            Math.abs(a.value - hintAspect) < 0.01;
          return (
            <button
              key={a.label}
              type="button"
              onClick={() => setAspect(a.value)}
              title={isHint ? "Обычный формат для этого блока" : undefined}
              className={`rounded px-2 py-1 text-xs ${
                aspect === a.value
                  ? "bg-primary/15 font-semibold"
                  : "text-on-surface-muted hover:text-on-surface"
              }`}
            >
              {a.label}
              {isHint ? " ·" : ""}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-on-surface-muted">
        {outOfRange
          ? `Кадр слишком ${cropAspect! > hintRange!.max ? "широкий" : "высокий"} для этого блока — по краям обрежется. Допустимо от ${hintRange!.min.toFixed(2)} до ${hintRange!.max.toFixed(2)} (ширина ÷ высота).`
          : "Блок подстроится под пропорции кадра — кропайте свободно."}
      </p>

      {/* Кнопки в две строки: в узкой панели «Отмена» раньше уезжала за край. */}
      <div className="flex flex-col gap-2">
        <Button onClick={() => void onApply()} disabled={disabled}>
          {disabled ? "Обработка…" : "Обрезать и загрузить"}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void onOriginal()}
            disabled={disabled}
            className="flex-1"
          >
            Загрузить оригинал
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={disabled}
            className="flex-1"
          >
            Отмена
          </Button>
        </div>
      </div>
    </div>
  );
}
